/**
 * The single place this app reads its environment from.
 *
 * Why this file exists
 * --------------------
 * Every NEXT_PUBLIC_* value is baked into the JavaScript bundle at build time,
 * inside the Docker builder stage, from --build-arg values that CD.yml fills
 * from branch-specific GitHub secrets. Nothing is read at container start.
 *
 * That makes a missing secret invisible. GitHub Actions substitutes an empty
 * string for a secret that is absent or misspelled; the empty string used to
 * land on a Dockerfile ARG default, and the ARG default was a staging URL. So a
 * production build with a broken secret compiled green, shipped, and quietly
 * talked to the staging backend -- and the old guard in config/api.ts could not
 * catch it, because it only asked whether a value was present, and a default is
 * present.
 *
 * So the rule here is not "is it set" but "is it the right kind of thing for the
 * environment it claims to be". A build that says production may not point at a
 * staging host, and vice versa. Every default has been deleted except the local
 * development ones, which are gated on NODE_ENV and can never reach an image.
 *
 * Why no Zod
 * ----------
 * config/frontend.ts re-exports this, and app/layout.tsx imports that, so this
 * module is in the shared client chunk of every single page. Pulling Zod into
 * that chunk to check three strings is a real cost for no benefit. Zod stays
 * where CLAUDE.md puts it -- request validation in lib/validation/schemas.ts.
 *
 * Why process.env.X is spelled out longhand below
 * -----------------------------------------------
 * Next substitutes the exact text `process.env.NEXT_PUBLIC_FOO` at build time.
 * Destructuring, indexing, or aliasing process.env defeats the substitution and
 * the value arrives as undefined in the browser. Leave these as they are.
 */

export const ENVIRONMENTS = ["production", "staging", "development"] as const;
export type Environment = (typeof ENVIRONMENTS)[number];

/**
 * Hosts that must never serve a production build, and -- inverted -- the marker
 * a staging build's backend is expected to carry. Both Azure App Services follow
 * the same naming convention today ("staging-rc-…" against "rc-…"), which is
 * what makes the cross-check possible. If staging is ever moved to a host
 * without "staging" in its name, change this constant; the error message points
 * back here.
 */
const STAGING_HOST_MARKER = "staging";
const LOCAL_HOSTS = ["localhost", "127.0.0.1", "0.0.0.0"];

// Used only when NODE_ENV is development or test. Never reachable from a build:
// `next build` runs as NODE_ENV=production, so a Docker image cannot pick these
// up however the build args are set.
const LOCAL_FALLBACK = {
  environment: "staging" as Environment,
  mobileBaseUrl:
    "https://staging-rc-e7d9fge6b9geaqcg.australiasoutheast-01.azurewebsites.net/api/mobile",
  webBaseUrl:
    "https://staging-rc-e7d9fge6b9geaqcg.australiasoutheast-01.azurewebsites.net/api/web",
};

export type RawEnv = {
  environment: string | undefined;
  mobileBaseUrl: string | undefined;
  webBaseUrl: string | undefined;
};

const isBlank = (value: string | undefined) => value === undefined || value.trim() === "";

function hostnameOf(value: string) {
  try {
    const url = new URL(value);
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;
    return url.hostname.toLowerCase();
  } catch {
    return null;
  }
}

const isLocalHost = (hostname: string) =>
  LOCAL_HOSTS.some((local) => hostname === local || hostname.endsWith(`.${local}`));

/**
 * Returns every problem it can find, rather than the first. A deploy that fails
 * on one missing secret, gets fixed, and then fails on the next one wastes two
 * pipeline runs to tell you something it knew the first time.
 */
export function validateEnv(raw: RawEnv): string[] {
  const problems: string[] = [];

  if (isBlank(raw.environment)) {
    problems.push(
      `NEXT_PUBLIC_ENV is empty. It must be one of ${ENVIRONMENTS.join(", ")}. ` +
        `An unlabelled build is the bug this check exists for: it used to default to ` +
        `"staging", which silently switched off indexing and analytics on production.`,
    );
  } else if (!ENVIRONMENTS.includes(raw.environment as Environment)) {
    problems.push(
      `NEXT_PUBLIC_ENV is "${raw.environment}", which is not one of ${ENVIRONMENTS.join(", ")}.`,
    );
  }

  const urls: Array<{ name: string; value: string | undefined }> = [
    { name: "NEXT_PUBLIC_RC_API_MOBILE_BASE_URL", value: raw.mobileBaseUrl },
    { name: "NEXT_PUBLIC_RC_API_WEB_BASE_URL", value: raw.webBaseUrl },
  ];

  for (const { name, value } of urls) {
    if (isBlank(value)) {
      problems.push(
        `${name} is empty. GitHub Actions substitutes an empty string for a secret ` +
          `that is missing or misspelled, so check the repository secret of that name first.`,
      );
      continue;
    }

    const hostname = hostnameOf(value as string);
    if (hostname === null) {
      problems.push(`${name} is "${value}", which is not an absolute http(s) URL.`);
      continue;
    }

    // The cross-check. This is the one that catches a wrong-but-plausible value,
    // which a presence check never can.
    if (raw.environment === "production") {
      if (hostname.includes(STAGING_HOST_MARKER)) {
        problems.push(
          `${name} points at "${hostname}", a staging host, but NEXT_PUBLIC_ENV is ` +
            `production. This is what a missing production secret looks like.`,
        );
      } else if (isLocalHost(hostname)) {
        problems.push(
          `${name} points at "${hostname}", which is not reachable from a container.`,
        );
      }
    }

    if (
      raw.environment === "staging" &&
      !hostname.includes(STAGING_HOST_MARKER) &&
      !isLocalHost(hostname)
    ) {
      problems.push(
        `${name} points at "${hostname}", which is not a staging host, but ` +
          `NEXT_PUBLIC_ENV is staging. A staging build wired to the live backend ` +
          `writes to live customer data. If staging has legitimately moved to this ` +
          `host, update STAGING_HOST_MARKER in config/env.ts.`,
      );
    }
  }

  return problems;
}

export function formatEnvProblems(problems: string[]): string {
  return [
    "",
    "  Environment configuration is invalid, so the build has been stopped.",
    "",
    ...problems.map((problem) => `    - ${problem}`),
    "",
    "  These values are compiled into the bundle at build time and cannot be",
    "  corrected afterwards, which is why this fails here rather than shipping an",
    "  image. Set them as --build-arg in the Dockerfile invocation; CD.yml maps",
    "  them from branch-specific repository secrets.",
    "",
  ].join("\n");
}

// next dev and vitest. A real build always runs as production, so these two are
// the only cases where a fallback is allowed to apply.
const isLocalRuntime = process.env.NODE_ENV === "development" || process.env.NODE_ENV === "test";

const raw: RawEnv = {
  environment: process.env.NEXT_PUBLIC_ENV,
  mobileBaseUrl: process.env.NEXT_PUBLIC_RC_API_MOBILE_BASE_URL,
  webBaseUrl: process.env.NEXT_PUBLIC_RC_API_WEB_BASE_URL,
};

if (!isLocalRuntime) {
  const problems = validateEnv(raw);
  if (problems.length > 0) {
    throw new Error(formatEnvProblems(problems));
  }
}

export const PUBLIC_ENV: Environment = isBlank(raw.environment)
  ? LOCAL_FALLBACK.environment
  : (raw.environment as Environment);

export const API_MOBILE_BASE_URL = isBlank(raw.mobileBaseUrl)
  ? LOCAL_FALLBACK.mobileBaseUrl
  : (raw.mobileBaseUrl as string);

export const API_WEB_BASE_URL = isBlank(raw.webBaseUrl)
  ? LOCAL_FALLBACK.webBaseUrl
  : (raw.webBaseUrl as string);

export const IsProduction = PUBLIC_ENV === "production";
export const IsStaging = PUBLIC_ENV === "staging";
export const IsDevelopment = PUBLIC_ENV === "development";
